"use client";

import {
  Form,
  FormSubmitButton,
  SheetFooter,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";

import ContactAddressesSection from "@/components/features/contact/ContactAddressesSection/ContactAddressesSection";
import ContactEmailsSection from "@/components/features/contact/ContactEmailsSection/ContactEmailsSection";
import ContactFaxesSection from "@/components/features/contact/ContactFaxesSection/ContactFaxesSection";
import ContactNotesSection from "@/components/features/contact/ContactNotesSection/ContactNotesSection";
import ContactPhoneNumbersSection from "@/components/features/contact/ContactPhoneNumbersSection/ContactPhoneNumbersSection";
import ContactTagConnectionsSection from "@/components/features/contact/ContactTagConnectionsSection/ContactTagConnectionsSection";
import ContactWebsitesSection from "@/components/features/contact/ContactWebsitesSection/ContactWebsitesSection";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  retrieveContactQueryOptions,
  useCreateContactAddress,
  useCreateContactEmail,
  useCreateContactFax,
  useCreateContactNote,
  useCreateContactPhone,
  useCreateContactTagConnection,
  useCreateContactWebsite,
  useDeleteContactAddress,
  useDeleteContactEmail,
  useDeleteContactFax,
  useDeleteContactNote,
  useDeleteContactPhone,
  useDeleteContactTagConnection,
  useDeleteContactWebsite,
  useUpdateContact,
  useUpdateContactAddress,
  useUpdateContactEmail,
  useUpdateContactFax,
  useUpdateContactNote,
  useUpdateContactPhone,
  useUpdateContactWebsite,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { GENDERS } from "@/lib/data/static/contactStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type Contact, type MatterContactConnection } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { toFormBoolean, toFormString } from "@/lib/utils/formFieldCoercion";
import {
  matterContactConnectionHeroTone,
  matterContactConnectionHeroToneFromMatterConnection,
} from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import { getlabel } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  type ReactNode,
} from "react";
import { useForm, useWatch } from "react-hook-form";
import type { z } from "zod";
import { ContactFormLivePreview } from "./ContactFormLivePreview";
import { ContactSubitemSection } from "./ContactSubitemSection";
import { createContactFormSchema } from "./contactFormSchema";
import { ContactIndividualFormSection } from "./ContactIndividualFormSection";
import { ContactOrganizationFormSection } from "./ContactOrganizationFormSection";
import { useDeepDirtyBaseline } from "./useDeepDirtyBaseline";

export interface EditContactFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  creatingMore?: boolean;
  activeContactId?: string;
  activeContact?: Contact;
  hideSubItems?: boolean;
  hasEditPermission?: boolean;
  handleSubmit?: (contact: Contact) => void;
  /** Omit footer (e.g. combined “Save” in a parent modal). */
  hideFooter?: boolean;
  /** When false, successful save does not call `setOpen(false)`. */
  closeOnSave?: boolean;
  /** Notifies parent when contact form dirty state changes (combined modals). */
  onDirtyChange?: (dirty: boolean) => void;
  /** For Slideover loading indicator. */
  onMutationPendingChange?: (pending: boolean) => void;
  fieldsetClassName?: string;
  formId?: string;
  /** Split layout with live preview (e.g. edit contact modal). */
  livePreviewLayout?: boolean;
  matterContactConnection?: MatterContactConnection;
  matterId?: string;
  /** Use the parent modal column as the scroll container when embedded in another form. */
  scrollContainer?: "self" | "parent";
  topContent?: ReactNode;
  bottomContent?: ReactNode;
  previewBottomContent?: ReactNode;
  /** Stages removing this contact from the owning record. */
  onRemoveContact?: () => void;
}

const defaultFormValues = {
  individualDateOfBirth: undefined,
  individualDeceased: false,
  individualExpertWitnessSpecialization: [],
  individualFirstName: null,
  individualGender: null,
  individualPrimaryLanguage: null,
  individualSecondaryLanguage: [],
  individualLastName: null,
  individualMiddleName: "",
  individualNickname: "",
  individualMinor: null,
  individualSmsConsent: null,
  individualTimezone: null,
  individualTitle: null,
  individualRace: [],
  individualSocialSecurityNumber: "",
  individualSubTypes: null,
  organizationFoundingYear: null,
  organizationMedicalProviderAcceptsLiens: null,
  organizationMedicalProviderSpecialization: [],
  organizationName: null,
  organizationTaxId: null,
  organizationSubTypes: null,
  groupName: null,
  groupDescription: "",
  preferredContactMethod: null,
  type: "individual",
} as const;

const toFormArray = <T,>(value: unknown) =>
  value == null || !Array.isArray(value) ? [] : [...(value as readonly T[])];

const CONTACT_FORM_ARRAY_ROOT_KEYS: (keyof z.infer<
  typeof createContactFormSchema
>)[] = [
  "emails",
  "phones",
  "faxes",
  "addresses",
  "websites",
  "notes",
  "tagConnections",
];
const INLINE_SUBITEM_TOAST_OPTIONS = { showSuccessToast: false } as const;

export type EditContactFormHandle = {
  /** Runs validation and submit; resolves true if `onSubmit` ran, false if validation failed. */
  submit: () => Promise<boolean>;
};

export const EditContactForm = forwardRef<
  EditContactFormHandle,
  EditContactFormProps
>(function EditContactForm(
  {
    open,
    setOpen,
    activeContactId,
    activeContact,
    hideSubItems,
    hasEditPermission,
    handleSubmit,
    hideFooter = false,
    closeOnSave = true,
    onDirtyChange,
    onMutationPendingChange,
    fieldsetClassName = slideoverFieldsetClassName,
    formId,
    livePreviewLayout = false,
    matterContactConnection,
    matterId,
    scrollContainer = "self",
    topContent,
    bottomContent,
    previewBottomContent,
    onRemoveContact,
  },
  ref,
) {
  const queryClient = useQueryClient();
  const resolvedContactId = activeContactId ?? activeContact?.id;
  const { data: contactData } = useQuery({
    ...retrieveContactQueryOptions(resolvedContactId),
    enabled: open && !activeContact && Boolean(resolvedContactId),
  });

  const { hasActionPermission } = useUser();

  const contact = activeContact ?? contactData?.data;
  const { data: matterData } = useQuery({
    ...retrieveMatterQueryOptions(matterId ?? ""),
    enabled: Boolean(matterId),
  });
  const contactFormLivePreviewTone = useMemo(() => {
    if (matterData?.data && matterContactConnection) {
      return matterContactConnectionHeroToneFromMatterConnection(
        matterData.data,
        matterContactConnection,
      );
    }
    return matterContactConnectionHeroTone(
      getlabel(matterContactConnection ?? {}),
    );
  }, [matterData?.data, matterContactConnection]);
  const formValues = useMemo<z.infer<typeof createContactFormSchema>>(() => {
    const currentContact = contact;

    // API payloads still return `null` for optional fields; coerce them to
    // form-safe values so unrelated edits do not fail validation.
    return {
      ...defaultFormValues,
      ...currentContact,
      individualDateOfBirth: currentContact?.individualDateOfBirth ?? undefined,
      individualDeceased: toFormBoolean(currentContact?.individualDeceased),
      individualDriverLicenseNumber: toFormString(
        currentContact?.individualDriverLicenseNumber,
      ),
      individualDriverLicenseState: toFormString(
        currentContact?.individualDriverLicenseState,
      ),
      individualFirstName:
        currentContact?.individualFirstName ??
        defaultFormValues.individualFirstName,
      individualGender: (GENDERS as readonly string[]).includes(
        currentContact?.individualGender ?? "",
      )
        ? currentContact?.individualGender
        : defaultFormValues.individualGender,
      individualPrimaryLanguage:
        currentContact?.individualPrimaryLanguage ??
        defaultFormValues.individualPrimaryLanguage,
      individualSecondaryLanguage: toFormArray(
        currentContact?.individualSecondaryLanguage,
      ),
      individualLastName:
        currentContact?.individualLastName ??
        defaultFormValues.individualLastName,
      individualMiddleName: toFormString(currentContact?.individualMiddleName),
      individualNickname: toFormString(currentContact?.individualNickname),
      individualMinor:
        currentContact?.individualMinor ?? defaultFormValues.individualMinor,
      individualSmsConsent:
        currentContact?.individualSmsConsent ??
        defaultFormValues.individualSmsConsent,
      individualTimezone:
        currentContact?.individualTimezone ??
        defaultFormValues.individualTimezone,
      individualTitle:
        currentContact?.individualTitle ?? defaultFormValues.individualTitle,
      individualRace: toFormArray(currentContact?.individualRace),
      individualSocialSecurityNumber: toFormString(
        currentContact?.individualSocialSecurityNumber,
      ),
      individualExpertWitnessSpecialization: toFormArray(
        currentContact?.individualExpertWitnessSpecialization,
      ),
      individualSubTypes:
        currentContact?.individualSubTypes ??
        defaultFormValues.individualSubTypes,
      organizationFoundingYear:
        currentContact?.organizationFoundingYear ??
        defaultFormValues.organizationFoundingYear,
      organizationMedicalProviderAcceptsLiens:
        currentContact?.organizationMedicalProviderAcceptsLiens ??
        defaultFormValues.organizationMedicalProviderAcceptsLiens,
      organizationMedicalProviderSpecialization: toFormArray(
        currentContact?.organizationMedicalProviderSpecialization,
      ),
      organizationName:
        currentContact?.organizationName ?? defaultFormValues.organizationName,
      organizationTaxId:
        currentContact?.organizationTaxId ??
        defaultFormValues.organizationTaxId,
      organizationSubTypes:
        currentContact?.organizationSubTypes ??
        defaultFormValues.organizationSubTypes,
      groupName: currentContact?.groupName ?? defaultFormValues.groupName,
      groupDescription: toFormString(currentContact?.groupDescription),
      title: toFormString(currentContact?.title),
      preferredContactMethod:
        currentContact?.preferredContactMethod ??
        defaultFormValues.preferredContactMethod,
      addresses: (currentContact?.addresses ?? []).map((address) => ({
        ...address,
        street1: toFormString(address.street1),
        street2: toFormString(address.street2),
        city: toFormString(address.city),
        county: address.county,
        state: toFormString(address.state),
        zipCode: toFormString(address.zipCode),
        country: toFormString(address.country) || "United States",
        description: toFormString(address.description),
        label: toFormString(address.label),
        isPrimary: toFormBoolean(address.isPrimary),
        dateModified: toFormString(address.dateModified),
      })),
      emails: (currentContact?.emails ?? []).map((email) => ({
        ...email,
        address: toFormString(email.address),
        description: toFormString(email.description),
        label: toFormString(email.label),
        isPrimary: toFormBoolean(email.isPrimary),
        dateModified: toFormString(email.dateModified),
      })),
      faxes: (currentContact?.faxes ?? []).map((fax) => ({
        ...fax,
        number: toFormString(fax.number),
        extension: toFormString(fax.extension),
        label: toFormString(fax.label),
        description: toFormString(fax.description),
        isPrimary: toFormBoolean(fax.isPrimary),
        dateModified: toFormString(fax.dateModified),
      })),
      notes: (currentContact?.notes ?? []).map((note) => ({
        ...note,
        message: toFormString(note.message),
        draft: toFormBoolean(note.draft),
        dateModified: toFormString(note.dateModified),
      })),
      phones: (currentContact?.phones ?? []).map((phone) => ({
        ...phone,
        number: toFormString(phone.number),
        extension: toFormString(phone.extension),
        label: toFormString(phone.label),
        description: toFormString(phone.description),
        isPrimary: toFormBoolean(phone.isPrimary),
        dateModified: toFormString(phone.dateModified),
      })),
      tagConnections: (currentContact?.tagConnections ?? []).map(
        (tagConnection) => ({
          ...tagConnection,
        }),
      ),
      websites: (currentContact?.websites ?? []).map((website) => ({
        ...website,
        address: toFormString(website.address),
        description: toFormString(website.description),
        label: toFormString(website.label),
        dateModified: toFormString(website.dateModified),
      })),
    };
  }, [contact]);

  const form = useForm<z.infer<typeof createContactFormSchema>>({
    resolver: zodResolver(createContactFormSchema),
    defaultValues: formValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const watchedValues = useWatch({ control: form.control });
  const {
    isDirty: isFormActuallyDirty,
    hasBaseline: hasDirtyBaseline,
    setBaselineSnapshot: setDirtyBaseline,
    clearBaseline: clearDirtyBaseline,
  } = useDeepDirtyBaseline({
    open,
    currentValues: watchedValues,
  });
  const updateContact = useUpdateContact();
  const createContactAddress = useCreateContactAddress(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const createContactEmail = useCreateContactEmail(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const createContactFax = useCreateContactFax(INLINE_SUBITEM_TOAST_OPTIONS);
  const createContactNote = useCreateContactNote(INLINE_SUBITEM_TOAST_OPTIONS);
  const createContactPhone = useCreateContactPhone(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const createContactTagConnection = useCreateContactTagConnection(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const createContactWebsite = useCreateContactWebsite(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const deleteContactAddress = useDeleteContactAddress(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const updateContactEmail = useUpdateContactEmail(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const deleteContactFax = useDeleteContactFax(INLINE_SUBITEM_TOAST_OPTIONS);
  const deleteContactNote = useDeleteContactNote(INLINE_SUBITEM_TOAST_OPTIONS);
  const deleteContactPhone = useDeleteContactPhone(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const deleteContactTagConnection = useDeleteContactTagConnection(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const deleteContactWebsite = useDeleteContactWebsite(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const updateContactAddress = useUpdateContactAddress(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const updateContactFax = useUpdateContactFax(INLINE_SUBITEM_TOAST_OPTIONS);
  const updateContactNote = useUpdateContactNote(INLINE_SUBITEM_TOAST_OPTIONS);
  const updateContactPhone = useUpdateContactPhone(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const updateContactWebsite = useUpdateContactWebsite(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const deleteContactEmail = useDeleteContactEmail(
    INLINE_SUBITEM_TOAST_OPTIONS,
  );
  const isFormLoading =
    updateContact.isPending ||
    createContactAddress.isPending ||
    createContactEmail.isPending ||
    createContactFax.isPending ||
    createContactNote.isPending ||
    createContactPhone.isPending ||
    createContactTagConnection.isPending ||
    createContactWebsite.isPending ||
    deleteContactAddress.isPending ||
    updateContactEmail.isPending ||
    deleteContactFax.isPending ||
    deleteContactNote.isPending ||
    deleteContactPhone.isPending ||
    deleteContactTagConnection.isPending ||
    deleteContactWebsite.isPending ||
    updateContactAddress.isPending ||
    updateContactFax.isPending ||
    updateContactNote.isPending ||
    updateContactPhone.isPending ||
    updateContactWebsite.isPending ||
    deleteContactEmail.isPending;

  const onSubmit = async (data: z.infer<typeof createContactFormSchema>) => {
    if (!contact) {
      return false;
    }
    const state = form.formState;
    const currentDirty = state.dirtyFields;
    const isAddressDirty = form.getFieldState("addresses", state).isDirty;
    const isEmailDirty = form.getFieldState("emails", state).isDirty;
    const isFaxDirty = form.getFieldState("faxes", state).isDirty;
    const isNoteDirty = form.getFieldState("notes", state).isDirty;
    const isPhoneDirty = form.getFieldState("phones", state).isDirty;
    const isTagDirty = form.getFieldState("tagConnections", state).isDirty;
    const isWebsiteDirty = form.getFieldState("websites", state).isDirty;
    const keys = new Set<keyof z.infer<typeof createContactFormSchema>>(
      Object.keys(currentDirty) as Array<
        keyof z.infer<typeof createContactFormSchema>
      >,
    );
    for (const k of CONTACT_FORM_ARRAY_ROOT_KEYS) {
      if (form.getFieldState(k, state).isDirty) {
        keys.add(k);
      }
    }
    const dirtyData: Partial<z.infer<typeof createContactFormSchema>> = {};
    for (const key of keys) {
      if (
        key === "addresses" ||
        key === "emails" ||
        key === "faxes" ||
        key === "notes" ||
        key === "phones" ||
        key === "tagConnections" ||
        key === "websites"
      ) {
        continue;
      }
      (dirtyData as Record<string, unknown>)[key] = data[key];
    }
    const isAnyNameDirty =
      "individualFirstName" in dirtyData ||
      "individualMiddleName" in dirtyData ||
      "individualLastName" in dirtyData;
    if (isAnyNameDirty) {
      dirtyData.individualFirstName = data.individualFirstName;
      dirtyData.individualMiddleName = data.individualMiddleName;
      dirtyData.individualLastName = data.individualLastName;
    }
    if ("individualDateOfBirth" in dirtyData) {
      dirtyData.individualDateOfBirth =
        data.individualDateOfBirth?.toString() ?? undefined;
    }
    const finalizeSave = async (fallbackContact: Contact) => {
      const refreshedContact =
        resolvedContactId != null
          ? await queryClient
              .fetchQuery(retrieveContactQueryOptions(resolvedContactId))
              .then((freshRes) => freshRes.data)
              .catch(() => fallbackContact)
          : fallbackContact;
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.matters],
        });
        handleSubmit?.(refreshedContact);
      }, 250);
      if (closeOnSave) {
        setOpen(false);
      }
      return true;
    };

    const syncContactAddresses = async () => {
      const originalAddresses = contact.addresses;
      const currentAddresses = data.addresses ?? [];
      const originalAddressMap = new Map(
        originalAddresses.map((address) => [address.id, address]),
      );
      const currentAddressIds = new Set(
        currentAddresses.map((address) => address.id),
      );

      for (const originalAddress of originalAddresses) {
        if (!currentAddressIds.has(originalAddress.id)) {
          await deleteContactAddress.mutateAsync({
            id: originalAddress.id,
            contactId: contact.id,
          });
        }
      }

      for (const currentAddress of currentAddresses) {
        const originalAddress = originalAddressMap.get(currentAddress.id);
        if (!originalAddress) {
          await createContactAddress.mutateAsync({
            city: currentAddress.city,
            country: currentAddress.country,
            description: currentAddress.description,
            isPrimary: currentAddress.isPrimary,
            label: currentAddress.label,
            state: currentAddress.state,
            street1: currentAddress.street1.trim() || null,
            street2: currentAddress.street2,
            zipCode: currentAddress.zipCode,
            contactId: contact.id,
            dateModified: undefined,
          });
          continue;
        }

        const addressChanged =
          toFormString(currentAddress.city) !==
            toFormString(originalAddress.city) ||
          toFormString(currentAddress.country) !==
            toFormString(originalAddress.country) ||
          toFormString(currentAddress.description) !==
            toFormString(originalAddress.description) ||
          toFormBoolean(currentAddress.isPrimary) !==
            toFormBoolean(originalAddress.isPrimary) ||
          toFormString(currentAddress.label) !==
            toFormString(originalAddress.label) ||
          toFormString(currentAddress.state) !==
            toFormString(originalAddress.state) ||
          toFormString(currentAddress.street1) !==
            toFormString(originalAddress.street1) ||
          toFormString(currentAddress.street2) !==
            toFormString(originalAddress.street2) ||
          toFormString(currentAddress.zipCode) !==
            toFormString(originalAddress.zipCode);

        if (!addressChanged) {
          continue;
        }

        await updateContactAddress.mutateAsync({
          city: currentAddress.city,
          country: currentAddress.country,
          description: currentAddress.description,
          id: originalAddress.id,
          isPrimary: currentAddress.isPrimary,
          label: currentAddress.label,
          state: currentAddress.state,
          street1: currentAddress.street1.trim() || null,
          street2: currentAddress.street2,
          zipCode: currentAddress.zipCode,
          contactId: contact.id,
          dateModified: originalAddress.dateModified,
        });
      }
    };

    const syncContactEmails = async () => {
      const originalEmails = contact.emails;
      const currentEmails = data.emails ?? [];
      const originalEmailMap = new Map(
        originalEmails.map((email) => [email.id, email]),
      );
      const currentEmailIds = new Set(currentEmails.map((email) => email.id));

      for (const originalEmail of originalEmails) {
        if (!currentEmailIds.has(originalEmail.id)) {
          await deleteContactEmail.mutateAsync({
            contactId: contact.id,
            id: originalEmail.id,
          });
        }
      }

      for (const currentEmail of currentEmails) {
        const originalEmail = originalEmailMap.get(currentEmail.id);
        if (!originalEmail) {
          await createContactEmail.mutateAsync({
            contactId: contact.id,
            address: currentEmail.address,
            isPrimary: currentEmail.isPrimary,
            label: currentEmail.label,
            dateModified: undefined,
          });
          continue;
        }

        const emailChanged =
          toFormString(currentEmail.address) !==
            toFormString(originalEmail.address) ||
          toFormBoolean(currentEmail.isPrimary) !==
            toFormBoolean(originalEmail.isPrimary) ||
          toFormString(currentEmail.label) !==
            toFormString(originalEmail.label);

        if (!emailChanged) {
          continue;
        }

        await updateContactEmail.mutateAsync({
          contactId: contact.id,
          id: originalEmail.id,
          address: currentEmail.address,
          isPrimary: currentEmail.isPrimary,
          label: currentEmail.label,
          dateModified: originalEmail.dateModified,
        });
      }
    };

    const syncContactFaxes = async () => {
      const originalFaxes = contact.faxes;
      const currentFaxes = data.faxes ?? [];
      const originalFaxMap = new Map(originalFaxes.map((fax) => [fax.id, fax]));
      const currentFaxIds = new Set(currentFaxes.map((fax) => fax.id));

      for (const originalFax of originalFaxes) {
        if (!currentFaxIds.has(originalFax.id)) {
          await deleteContactFax.mutateAsync({
            id: originalFax.id,
            contactId: contact.id,
          });
        }
      }

      for (const currentFax of currentFaxes) {
        const originalFax = originalFaxMap.get(currentFax.id);
        if (!originalFax) {
          await createContactFax.mutateAsync({
            description: currentFax.description,
            extension: currentFax.extension,
            isPrimary: currentFax.isPrimary,
            label: currentFax.label,
            number: currentFax.number,
            contactId: contact.id,
            dateModified: undefined,
          });
          continue;
        }

        const faxChanged =
          toFormString(currentFax.description) !==
            toFormString(originalFax.description) ||
          toFormString(currentFax.extension) !==
            toFormString(originalFax.extension) ||
          toFormBoolean(currentFax.isPrimary) !==
            toFormBoolean(originalFax.isPrimary) ||
          toFormString(currentFax.label) !== toFormString(originalFax.label) ||
          toFormString(currentFax.number) !== toFormString(originalFax.number);

        if (!faxChanged) {
          continue;
        }

        await updateContactFax.mutateAsync({
          description: currentFax.description,
          extension: currentFax.extension,
          id: originalFax.id,
          isPrimary: currentFax.isPrimary,
          label: currentFax.label,
          number: currentFax.number,
          contactId: contact.id,
          dateModified: originalFax.dateModified,
        });
      }
    };

    const syncContactNotes = async () => {
      const originalNotes = contact.notes;
      const currentNotes = data.notes ?? [];
      const originalNoteMap = new Map(
        originalNotes.map((note) => [note.id, note]),
      );
      const currentNoteIds = new Set(currentNotes.map((note) => note.id));

      for (const originalNote of originalNotes) {
        if (!currentNoteIds.has(originalNote.id)) {
          await deleteContactNote.mutateAsync({
            contactId: contact.id,
            id: originalNote.id,
          });
        }
      }

      for (const currentNote of currentNotes) {
        const originalNote = originalNoteMap.get(currentNote.id);
        if (!originalNote) {
          await createContactNote.mutateAsync({
            contactId: contact.id,
            draft: currentNote.draft,
            message: currentNote.message,
          });
          continue;
        }

        const noteChanged =
          toFormBoolean(currentNote.draft) !==
            toFormBoolean(originalNote.draft) ||
          toFormString(currentNote.message) !==
            toFormString(originalNote.message);

        if (!noteChanged) {
          continue;
        }

        await updateContactNote.mutateAsync({
          contactId: contact.id,
          draft: currentNote.draft,
          id: originalNote.id,
          message: currentNote.message,
          dateModified: originalNote.dateModified,
        });
      }
    };

    const syncContactPhones = async () => {
      const originalPhones = contact.phones;
      const currentPhones = data.phones ?? [];
      const originalPhoneMap = new Map(
        originalPhones.map((phone) => [phone.id, phone]),
      );
      const currentPhoneIds = new Set(currentPhones.map((phone) => phone.id));

      for (const originalPhone of originalPhones) {
        if (!currentPhoneIds.has(originalPhone.id)) {
          await deleteContactPhone.mutateAsync({
            contactId: contact.id,
            id: originalPhone.id,
          });
        }
      }

      for (const currentPhone of currentPhones) {
        const originalPhone = originalPhoneMap.get(currentPhone.id);
        if (!originalPhone) {
          await createContactPhone.mutateAsync({
            contactId: contact.id,
            description: currentPhone.description,
            extension: currentPhone.extension,
            isPrimary: currentPhone.isPrimary,
            label: currentPhone.label,
            number: currentPhone.number,
            dateModified: undefined,
          });
          continue;
        }

        const phoneChanged =
          toFormString(currentPhone.description) !==
            toFormString(originalPhone.description) ||
          toFormString(currentPhone.extension) !==
            toFormString(originalPhone.extension) ||
          toFormBoolean(currentPhone.isPrimary) !==
            toFormBoolean(originalPhone.isPrimary) ||
          toFormString(currentPhone.label) !==
            toFormString(originalPhone.label) ||
          toFormString(currentPhone.number) !==
            toFormString(originalPhone.number);

        if (!phoneChanged) {
          continue;
        }

        await updateContactPhone.mutateAsync({
          contactId: contact.id,
          description: currentPhone.description,
          extension: currentPhone.extension,
          id: originalPhone.id,
          isPrimary: currentPhone.isPrimary,
          label: currentPhone.label,
          number: currentPhone.number,
          dateModified: originalPhone.dateModified,
        });
      }
    };

    const syncContactTagConnections = async () => {
      const originalTags = contact.tagConnections;
      const currentTags = data.tagConnections ?? [];
      const originalTagMap = new Map(
        originalTags.map((tag) => [tag.tagId, tag]),
      );
      const currentTagIds = new Set(currentTags.map((tag) => tag.tagId));

      for (const originalTag of originalTags) {
        if (!currentTagIds.has(originalTag.tagId)) {
          await deleteContactTagConnection.mutateAsync({
            contactId: contact.id,
            id: originalTag.id,
          });
        }
      }

      for (const currentTag of currentTags) {
        if (originalTagMap.has(currentTag.tagId)) {
          continue;
        }
        await createContactTagConnection.mutateAsync({
          contactId: contact.id,
          tagId: currentTag.tagId,
          dateModified: undefined,
        });
      }
    };

    const syncContactWebsites = async () => {
      const originalWebsites = contact.websites;
      const currentWebsites = data.websites ?? [];
      const originalWebsiteMap = new Map(
        originalWebsites.map((website) => [website.id, website]),
      );
      const currentWebsiteIds = new Set(
        currentWebsites.map((website) => website.id),
      );

      for (const originalWebsite of originalWebsites) {
        if (!currentWebsiteIds.has(originalWebsite.id)) {
          await deleteContactWebsite.mutateAsync({
            contactId: contact.id,
            id: originalWebsite.id,
          });
        }
      }

      for (const currentWebsite of currentWebsites) {
        const originalWebsite = originalWebsiteMap.get(currentWebsite.id);
        if (!originalWebsite) {
          await createContactWebsite.mutateAsync({
            contactId: contact.id,
            address: currentWebsite.address,
            description: currentWebsite.description,
            label: currentWebsite.label,
            dateModified: undefined,
          });
          continue;
        }

        const websiteChanged =
          toFormString(currentWebsite.address) !==
            toFormString(originalWebsite.address) ||
          toFormString(currentWebsite.description) !==
            toFormString(originalWebsite.description) ||
          toFormString(currentWebsite.label) !==
            toFormString(originalWebsite.label);

        if (!websiteChanged) {
          continue;
        }

        await updateContactWebsite.mutateAsync({
          contactId: contact.id,
          address: currentWebsite.address,
          description: currentWebsite.description,
          id: originalWebsite.id,
          label: currentWebsite.label,
          dateModified: originalWebsite.dateModified,
        });
      }
    };

    if (isAddressDirty) {
      await syncContactAddresses();
    }
    if (isEmailDirty) {
      await syncContactEmails();
    }
    if (isFaxDirty) {
      await syncContactFaxes();
    }
    if (isNoteDirty) {
      await syncContactNotes();
    }
    if (isPhoneDirty) {
      await syncContactPhones();
    }
    if (isTagDirty) {
      await syncContactTagConnections();
    }
    if (isWebsiteDirty) {
      await syncContactWebsites();
    }

    if (Object.keys(dirtyData).length === 0) {
      return finalizeSave(contact);
    }

    const res = await updateContact.mutateAsync({
      ...dirtyData,
      id: resolvedContactId ?? contact.id,
      dateModified: contact.dateModified,
    } as Parameters<typeof updateContact.mutateAsync>[0]);

    return finalizeSave(res.data);
  };

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise<boolean>((resolve) => {
        void form.handleSubmit(
          async (data) => {
            try {
              const ok = await onSubmit(data);
              resolve(ok);
            } catch {
              resolve(false);
            }
          },
          () => {
            resolve(false);
          },
        )();
      }),
  }));

  useEffect(() => {
    if (!open) {
      form.reset();
      clearDirtyBaseline();
      return;
    }

    form.reset(formValues);
    clearDirtyBaseline();
  }, [clearDirtyBaseline, form, formValues, open]);

  useEffect(() => {
    if (!open || hasDirtyBaseline) {
      return;
    }

    setDirtyBaseline(form.getValues());
  }, [form, hasDirtyBaseline, open, setDirtyBaseline, watchedValues]);

  useEffect(() => {
    onDirtyChange?.(isFormActuallyDirty);
  }, [isFormActuallyDirty, onDirtyChange]);

  useEffect(() => {
    onMutationPendingChange?.(isFormLoading);
  }, [isFormLoading, onMutationPendingChange]);

  const type = form.watch("type");
  const isGroupContact = type === "group";

  const resolvedFormId = useMemo(() => formId ?? "edit-contact-form", [formId]);
  const useParentScroll = scrollContainer === "parent";

  return (
    <Form {...form}>
      <form
        id={resolvedFormId}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit(onSubmit)();
        }}
        className={cn(
          "flex min-w-0",
          useParentScroll ? "w-full overflow-visible" : "min-h-0",
          livePreviewLayout
            ? "h-full max-h-full w-full flex-1 flex-col overflow-hidden lg:flex-row"
            : useParentScroll
              ? "flex-col"
              : "h-full grow flex-col",
        )}
      >
        {livePreviewLayout ? (
          <aside
            className={cn(
              "border-border flex max-h-[min(40vh,320px)] min-h-0 shrink-0 flex-col overflow-hidden border-b",
              "lg:max-h-full lg:w-[min(100%,380px)] lg:max-w-[380px] lg:self-stretch lg:border-r lg:border-b-0",
            )}
          >
            <div
              className="h-0 min-h-0 flex-1 scroll-py-2 overflow-y-auto overscroll-contain px-5 pt-3 pb-4"
              onWheel={(e) => {
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
                Preview
              </p>
              <ContactFormLivePreview
                matterContactConnection={matterContactConnection}
                matterId={matterId}
                slotLabel={getlabel(matterContactConnection ?? {}) || "Contact"}
                tone={contactFormLivePreviewTone}
                onRemoveContact={onRemoveContact}
              />
              {previewBottomContent}
            </div>
          </aside>
        ) : null}
        <div
          className={cn(
            "relative flex min-w-0 flex-col",
            useParentScroll
              ? "w-full overflow-visible"
              : "h-full min-h-0 flex-1",
            livePreviewLayout && "overflow-hidden",
            !livePreviewLayout && !useParentScroll && "h-full min-h-0",
          )}
        >
          {isFormLoading && <FormLoadingOverlay />}
          <div
            className={cn(
              useParentScroll
                ? "w-full overflow-visible"
                : "min-h-0 flex-1 overflow-y-auto overscroll-contain",
              livePreviewLayout
                ? "h-0 scroll-py-2 px-3 pt-3"
                : useParentScroll
                  ? ""
                  : "h-full",
              livePreviewLayout && hideFooter && "pb-6",
            )}
            onWheel={
              livePreviewLayout
                ? (e) => {
                    e.stopPropagation();
                  }
                : undefined
            }
            onTouchMove={
              livePreviewLayout
                ? (e) => {
                    e.stopPropagation();
                  }
                : undefined
            }
          >
            {topContent}
            <fieldset
              disabled={!hasEditPermission}
              className={fieldsetClassName}
            >
              <SimpleSelectFormField
                register={form.register("type")}
                label="Contact type*"
                items={["individual", "organization", "group"]}
                disabled={true}
              />
              {type === "individual" && <ContactIndividualFormSection />}
              {type === "organization" && <ContactOrganizationFormSection />}
              {isGroupContact && (
                <>
                  <TextFormField
                    register={form.register("groupName")}
                    label="Group name*"
                    dataTestId="contact-group-name"
                  />
                  <div className="col-span-2">
                    <TextAreaFormField
                      register={form.register("groupDescription")}
                      label="Group description"
                    />
                  </div>
                </>
              )}
            </fieldset>
            {!hideSubItems && !isGroupContact && (
              <div className="col-span-full mb-4 px-2 py-4">
                <ContactSubitemSection
                  title="Contact emails"
                  count={(form.watch("emails") ?? []).length}
                >
                  <ContactEmailsSection
                    hideLabel
                    parentForm={form}
                    emails={contact?.emails}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactEmail,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactEmail,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactEmail,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                    organizationSubTypes={
                      form.watch("organizationSubTypes") ?? undefined
                    }
                    type={form.watch("type") ?? undefined}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact addresses"
                  count={(form.watch("addresses") ?? []).length}
                >
                  <ContactAddressesSection
                    hideLabel
                    collapseMultiple
                    parentForm={form}
                    addresses={contact?.addresses}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactAddress,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactAddress,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactAddress,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact phones"
                  count={(form.watch("phones") ?? []).length}
                >
                  <ContactPhoneNumbersSection
                    hideLabel
                    parentForm={form}
                    phoneNumbers={contact?.phones}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactPhone,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactPhone,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactPhone,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                    organizationSubTypes={
                      form.watch("organizationSubTypes") ?? undefined
                    }
                    type={form.watch("type") ?? undefined}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact faxes"
                  count={(form.watch("faxes") ?? []).length}
                >
                  <ContactFaxesSection
                    hideLabel
                    parentForm={form}
                    faxes={contact?.faxes}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactFax,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactFax,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactFax,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact websites"
                  count={(form.watch("websites") ?? []).length}
                >
                  <ContactWebsitesSection
                    hideLabel
                    compact
                    parentForm={form}
                    websites={contact?.websites}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactWebsite,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactWebsite,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactWebsite,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <ContactSubitemSection
                  title="Contact notes"
                  count={(form.watch("notes") ?? []).length}
                >
                  <ContactNotesSection
                    hideLabel
                    parentForm={form}
                    notes={contact?.notes}
                    comments={contact?.noteComments}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasEditPermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.update,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </ContactSubitemSection>

                <div className="mt-4">
                  <ContactTagConnectionsSection
                    parentForm={form}
                    tagConnections={contact?.tagConnections}
                    contactId={contact?.id ?? ""}
                    hasCreatePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactTagConnection,
                      ACTION_PERMISSION_VALUES.create,
                    )}
                    hasDeletePermission={hasActionPermission(
                      ACTION_PERMISSION_KEYS.contactTagConnection,
                      ACTION_PERMISSION_VALUES.delete,
                    )}
                  />
                </div>
              </div>
            )}
            {bottomContent}
          </div>
          {!hideFooter ? (
            <SheetFooter addSlideoverStyles>
              <div className="flex justify-end space-x-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={isFormActuallyDirty}
                />
                <FormSubmitButton
                  loading={isFormLoading}
                  disabled={!isFormActuallyDirty}
                />
              </div>
            </SheetFooter>
          ) : null}
        </div>
      </form>
    </Form>
  );
});
